import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { useState } from 'react';

import {
  CardTitle,
  Container,
  CustomTableContainer,
  Footer,
  TitleContainer,
} from './styles';
import { PaginationButtons } from './components';
import { Loading } from '../Loading';
import TableBody from '@mui/material/TableBody';

export interface HeadCell {
  key?: string;
  label: string;
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  heads: HeadCell[];
  hasCheckbox?: boolean;
  isChecked: boolean;
  setIsChecked?: React.Dispatch<React.SetStateAction<boolean>>;
}

export type Order = 'asc' | 'desc';

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    order,
    orderBy,
    onRequestSort,
    heads,
    hasCheckbox,
    isChecked,
    setIsChecked,
  } = props;
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead style={{ position: 'relative' }}>
      {hasCheckbox && (
        <input
          style={{
            position: 'absolute',
            left: '21px',
            top: '18px',
            width: '16px',
            height: '16px',
            border: 'none',
            textDecoration: 'none',
            outline: 'none',
          }}
          type="checkbox"
          onChange={() => setIsChecked && setIsChecked(!isChecked)}
          checked={isChecked}
        />
      )}
      <TableRow>
        {heads.map((headCell) => (
          <TableCell
            key={headCell.key || headCell.label}
            padding="none"
            sortDirection={orderBy === headCell.key ? order : false}
            style={{ textAlign: 'center' }}
          >
            {headCell.key ? (
              <TableSortLabel
                hideSortIcon={orderBy !== headCell.key}
                active={orderBy === headCell.key}
                direction={orderBy === headCell.key ? order : 'asc'}
                onClick={createSortHandler(headCell.key)}
              >
                {headCell.label}
                {orderBy === headCell.key ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
interface ITable {
  title: string;
  heads: HeadCell[];
  children?: JSX.Element[] | JSX.Element;
  hasPagination: boolean;
  itemsPerPage?: number;
  setItemsPerPage?: React.Dispatch<React.SetStateAction<number>>;
  page?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  totalItems?: number;
  nPages?: number;
  data?: any[];
  order: Order;
  setOrder: React.Dispatch<React.SetStateAction<Order>> | any;
  orderBy: string;
  setOrderBy: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  error: any;
  hasCheckbox?: boolean;
  isChecked?: boolean;
  setIsChecked?: () => void;
}

export default function NewDataTable({
  children,
  title,
  heads,
  data,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  page,
  setPage,
  totalItems,
  nPages,
  hasPagination,
  itemsPerPage,
  setItemsPerPage,
  isLoading,
  error,
  hasCheckbox,
  isChecked = false,
  setIsChecked,
}: ITable) {
  const [selected, setSelected] = useState<readonly string[]>([]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = heads.map((n) => n.label);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleRequestSort = (
    event: React.MouseEvent<any>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      <Container>
        <TitleContainer>
          <CardTitle>
            <h2>{title}</h2>
          </CardTitle>
        </TitleContainer>

        <CustomTableContainer>
          <Paper>
            <TableContainer>
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size="medium"
              >
                <EnhancedTableHead
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={data?.length ?? 0}
                  heads={heads}
                  hasCheckbox={hasCheckbox}
                  isChecked={isChecked}
                  setIsChecked={setIsChecked}
                />
                {isLoading ? (
                  <Loading />
                ) : error ? (
                  <TableBody>
                    <tr>
                      <td colSpan={17}>Falha ao obter dados</td>
                    </tr>
                  </TableBody>
                ) : data?.length === 0 ? (
                  <tr>
                    <td colSpan={17}>
                      Não foram encontrados dados{' '}
                      {title.includes('Blocked List -') && 'no mês atual'}
                    </td>
                  </tr>
                ) : (
                  children
                )}
              </Table>
            </TableContainer>
          </Paper>
        </CustomTableContainer>
      </Container>
      {hasPagination && (
        <Footer>
          <PaginationButtons
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            nPages={nPages}
            totalItems={totalItems}
          />
        </Footer>
      )}
    </>
  );
}
